import React, { useState } from "react";

/**
 * Iter Q — Extracted from AdminDashboard.jsx (was ~1446 lines).
 *
 * All original behaviour preserved verbatim:
 *   - Same test IDs (s-notification-emails*, s-notification-whatsapp-numbers*,
 *     s-notification-email-enabled, s-notification-whatsapp-enabled).
 *   - Same styling.
 *   - Same save contract: parent's `s` state holds the four fields; parent
 *     already POSTs the whole payload on Save.
 *   - Helpers (SettingsGroup, ToggleField) are re-exported from here so the
 *     parent can still consume them, keeping the diff surgical.
 */


// ---------- SettingsGroup (moved from AdminDashboard.jsx) ----------
export function SettingsGroup({ title, accent = "black", children }) {
  const border = accent === "red"
    ? "border-[var(--panini-red)]"
    : accent === "yellow"
      ? "border-[var(--panini-yellow)]"
      : accent === "green"
        ? "border-emerald-700"
        : "border-[var(--ink)]";
  return (
    <section className={`rounded-2xl border-2 ${border} bg-white p-5 shadow-[6px_6px_0_var(--ink)] space-y-4`}>
      <h2 className="font-display text-xl">{title}</h2>
      {children}
    </section>
  );
}


// ---------- ToggleField (moved from AdminDashboard.jsx) ----------
export function ToggleField({ label, value, onChange, testid }) {
  return (
    <label className="block cursor-pointer select-none">
      <div className="kicker kicker-black text-[11px] mb-2">{label}</div>
      <button
        type="button"
        role="switch"
        aria-checked={!!value}
        data-testid={testid}
        onClick={() => onChange(!value)}
        className={`w-full flex items-center justify-between gm-field !py-2.5 !cursor-pointer border-2 ${
          value ? "border-emerald-700 bg-emerald-50" : "border-[var(--ink)] bg-white"
        }`}
      >
        <span className="text-sm font-bold text-[var(--ink)]">
          {value ? "Enabled" : "Disabled"}
        </span>
        <span
          className={`inline-flex items-center justify-center w-10 h-6 rounded-full border-2 border-[var(--ink)] transition ${
            value ? "bg-emerald-500" : "bg-neutral-300"
          }`}
        >
          <span
            className={`w-4 h-4 rounded-full bg-white border-2 border-[var(--ink)] transform transition ${
              value ? "translate-x-2" : "-translate-x-2"
            }`}
          />
        </span>
      </button>
    </label>
  );
}


// ---------- RecipientListField (moved from AdminDashboard.jsx) ----------
export function RecipientListField({ label, values, onChange, testid, placeholder, hint, validator }) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  const list = Array.isArray(values) ? values : [];

  const commit = () => {
    const v = (draft || "").trim();
    if (!v) return;
    if (validator) {
      const err = validator(v);
      if (err) { setError(err); return; }
    }
    if (list.includes(v)) { setError("Already in list"); return; }
    onChange([...list, v]);
    setDraft("");
    setError("");
  };
  const removeAt = (i) => {
    const next = list.slice();
    next.splice(i, 1);
    onChange(next);
  };

  return (
    <label className="block">
      <div className="kicker kicker-black text-[11px] mb-2">{label}</div>
      <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`${testid}-chips`}>
        {list.length === 0 ? (
          <span className="text-xs text-[var(--smoke)] italic">No recipients yet.</span>
        ) : list.map((v, i) => (
          <span key={`${v}-${i}`}
                className="inline-flex items-center gap-1 border-2 border-[var(--ink)] bg-[var(--panini-yellow)] rounded-full pl-2 pr-1 py-0.5 text-xs font-bold"
                data-testid={`${testid}-chip-${i}`}>
            <span className="font-mono">{v}</span>
            <button type="button" onClick={() => removeAt(i)}
                    className="w-4 h-4 rounded-full bg-[var(--ink)] text-white text-[10px] leading-none flex items-center justify-center hover:bg-[var(--panini-red)]"
                    aria-label={`Remove ${v}`} data-testid={`${testid}-remove-${i}`}>×</button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => { setDraft(e.target.value); setError(""); }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commit(); }
            if (e.key === "Backspace" && !draft && list.length) removeAt(list.length - 1);
          }}
          placeholder={placeholder}
          className="gm-field flex-1"
          data-testid={`${testid}-input`}
        />
        <button type="button" onClick={commit}
                className="btn-yellow text-sm px-3"
                data-testid={`${testid}-add`}>Add</button>
      </div>
      {error && <div className="text-xs text-[var(--panini-red)] mt-1">{error}</div>}
      {hint && !error && <div className="text-xs text-[var(--smoke)] mt-1">{hint}</div>}
    </label>
  );
}


// ---------- The section itself (public export) ----------
export default function SettingsNotificationsSection({ s, set }) {
  const emailEnabled = s.notification_email_enabled === undefined ? true : !!s.notification_email_enabled;
  const whatsappEnabled = s.notification_whatsapp_enabled === undefined ? true : !!s.notification_whatsapp_enabled;
  return (
    <SettingsGroup title="Notifications" accent="red">
      <p className="text-sm text-[var(--smoke)] -mt-2">
        Recipients receive admin alerts when a customer submits payment. Add multiple emails or
        WhatsApp numbers — each is notified independently. Toggle a channel off to silence it without
        losing the configured recipients.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-start">
        <RecipientListField
          label="Admin notification emails"
          values={s.notification_emails || []}
          onChange={set("notification_emails")}
          testid="s-notification-emails"
          placeholder="orders@collectorup.com"
          hint="Add one per email. Press Enter or click Add."
          validator={(v) => /.+@.+\..+/.test(v) ? null : "Invalid email"}
        />
        <ToggleField
          label="Enable email notifications"
          value={emailEnabled}
          onChange={set("notification_email_enabled")}
          testid="s-notification-email-enabled"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-start">
        <RecipientListField
          label="Admin WhatsApp numbers (E.164)"
          values={s.notification_whatsapp_numbers || []}
          onChange={set("notification_whatsapp_numbers")}
          testid="s-notification-whatsapp-numbers"
          placeholder="+17865756919"
          hint="One number per entry. Include country code (E.164)."
          validator={(v) => /^\+?[0-9\s\-()]{8,20}$/.test(v) ? null : "Invalid phone"}
        />
        <ToggleField
          label="Enable WhatsApp notifications"
          value={whatsappEnabled}
          onChange={set("notification_whatsapp_enabled")}
          testid="s-notification-whatsapp-enabled"
        />
      </div>
      <p className="text-xs text-[var(--smoke)]">
        WhatsApp integration ships in a dedicated iteration. Until then, enabling this channel is a no-op — the
        system logs <code>WhatsApp provider not configured.</code> and continues without blocking the email alerts.
      </p>
    </SettingsGroup>
  );
}
